/**
 * Smart Patient Intake™ — Premium document scan + intake wizard.
 *
 * Three-step flow:
 *   1. Capture: webcam / file upload / drag-drop · mobile auto-capture
 *   2. Review: OCR data preview with inline edit · CF validation badge · dedup warning
 *   3. Done: confirmation + link to new client profile
 *
 * Design inspirations: Apple Health, Stripe checkout, Linear settings.
 * Sector-gated: dental only.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "../../App";
import { useIsDentalTenant } from "../../hooks/useIsDentalTenant";
import { useIsProTenant }    from "../../hooks/useIsProTenant";
import {
  ArrowLeft, ArrowRight, Camera, Upload, ScanLine, CheckCircle2, AlertTriangle,
  Loader2, Crown, Sparkles, ShieldCheck, User as UserIcon, X, RefreshCw,
  Calendar, MapPin, Mail, Phone, FileText, BadgeCheck, AlertCircle, Smartphone,
} from "lucide-react";
import { toast } from "sonner";

const PatientIntakePage = () => {
  const { user, loading: authLoading } = useAuth();
  const isDental = useIsDentalTenant();
  const isPro    = useIsProTenant();
  const canScanIntake = isDental || isPro;
  const navigate = useNavigate();

  const [step, setStep] = useState(1);          // 1 capture · 2 review · 3 done
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [ocrData, setOcrData] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newClient, setNewClient] = useState(null);

  // Camera state
  const [cameraOn, setCameraOn] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);

  // Gate
  useEffect(() => {
    if (!authLoading && user && !canScanIntake) {
      toast.error("Funzionalità inclusa nei piani Pro · effettua l'upgrade per attivarla");
      navigate("/dashboard");
    }
  }, [authLoading, user, canScanIntake, navigate]);

  /* ---------- Webcam ---------- */
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },   // back camera on mobile
          width: { ideal: 1920 }, height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOn(true);
      // Attach to <video> on next frame
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 50);
    } catch (e) {
      toast.error("Impossibile accedere alla fotocamera: " + (e.message || e));
    }
  };
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
  };
  useEffect(() => () => stopCamera(), []);

  const captureFromVideo = async () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width  = videoRef.current.videoWidth || 1280;
    canvas.height = videoRef.current.videoHeight || 720;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      stopCamera();
      const file = new File([blob], "tessera.jpg", { type: "image/jpeg" });
      setPreviewUrl(URL.createObjectURL(blob));
      await runOCR(file);
    }, "image/jpeg", 0.92);
  };

  /* ---------- OCR ---------- */
  const runOCR = async (file) => {
    setScanning(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/dental/intake/scan`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" },
      });
      setOcrData(r.data);
      // Pre-check duplicates
      try {
        const dr = await axios.post(`${API}/dental/intake/check-dup`, {
          fiscal_code: r.data.fiscal_code || undefined,
          first_name: r.data.first_name, last_name: r.data.last_name,
        }, { withCredentials: true });
        setDuplicates(dr.data?.duplicates || []);
      } catch (e) {
        console.warn("[Intake] duplicate check failed", e);
      }
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OCR fallito · riprova con un'immagine più nitida");
    } finally {
      setScanning(false);
    }
  };

  const onFile = (f) => {
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Immagine troppo grande (max 8MB)"); return; }
    setPreviewUrl(URL.createObjectURL(f));
    runOCR(f);
  };

  /* ---------- Save ---------- */
  const saveClient = async () => {
    if (!ocrData) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/dental/intake/create`, ocrData, {
        withCredentials: true,
      });
      setNewClient(r.data.client);
      setStep(3);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.duplicate) {
        toast.error("Paziente già presente: " + d.duplicate.first_name + " " + d.duplicate.last_name);
        setDuplicates([d.duplicate, ...duplicates]);
      } else {
        toast.error(typeof d === "string" ? d : "Errore salvataggio");
      }
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setStep(1); setOcrData(null); setDuplicates([]); setPreviewUrl(null);
    setNewClient(null); stopCamera();
  };

  if (authLoading || !user || !canScanIntake) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="intake-page">
      {/* HEADER */}
      <div className="border-b border-slate-200/60 dark:border-white/5 bg-white/80 dark:bg-slate-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/dashboard" className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5" data-testid="intake-back">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-violet-600 to-cyan-600 text-white text-[9px] font-bold uppercase tracking-[0.18em]">
                <Crown className="w-2.5 h-2.5" /> Dental Suite
              </span>
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-wider">
                <Sparkles className="w-2.5 h-2.5 inline mr-0.5" /> Smart Patient Intake™
              </span>
            </div>
            <h1 className="text-2xl font-black tracking-tight mt-1">Nuovo paziente in 30 secondi.</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Scansiona la tessera sanitaria · estrazione AI · GDPR ready
            </p>
          </div>
          <Stepper step={step} />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {step === 1 && (
          <CaptureStep
            cameraOn={cameraOn}
            videoRef={videoRef}
            startCamera={startCamera} stopCamera={stopCamera}
            captureFromVideo={captureFromVideo}
            scanning={scanning}
            fileRef={fileRef} onFile={onFile}
          />
        )}
        {step === 2 && ocrData && (
          <ReviewStep
            data={ocrData} setData={setOcrData}
            previewUrl={previewUrl}
            duplicates={duplicates}
            onBack={reset}
            onConfirm={saveClient}
            saving={saving}
          />
        )}
        {step === 3 && newClient && (
          <DoneStep client={newClient} onReset={reset} />
        )}
      </div>
    </div>
  );
};

/* ---------- Stepper ---------- */
const Stepper = ({ step }) => {
  const items = ["Scan", "Review", "Done"];
  return (
    <div className="hidden sm:flex items-center gap-1.5" data-testid="intake-stepper">
      {items.map((label, i) => {
        const n = i + 1;
        const done = step > n;
        const current = step === n;
        return (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${
              current ? "bg-gradient-to-br from-violet-600 to-cyan-600 text-white shadow"
                : done ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                : "bg-slate-100 dark:bg-white/5 text-slate-400"
            }`}>
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : n}
            </div>
            <span className={`text-[11px] font-semibold ${current ? "text-slate-900 dark:text-white" : "text-slate-400"}`}>{label}</span>
            {i < items.length - 1 && <span className="w-6 h-px bg-slate-200 dark:bg-white/10 mx-1" />}
          </div>
        );
      })}
    </div>
  );
};

/* ---------- Step 1: Capture ---------- */
const CaptureStep = ({ cameraOn, videoRef, startCamera, stopCamera, captureFromVideo, scanning, fileRef, onFile }) => {
  const [dragOver, setDragOver] = useState(false);
  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6">
      <div className="space-y-4">
        {/* Camera live preview OR drop zone */}
        {cameraOn ? (
          <div className="relative rounded-3xl overflow-hidden bg-slate-950 aspect-[16/10]" data-testid="intake-camera">
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            {/* Corner brackets overlay (premium scan-frame look) */}
            <div className="absolute inset-6 pointer-events-none">
              <Bracket corner="tl" /> <Bracket corner="tr" />
              <Bracket corner="bl" /> <Bracket corner="br" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white/90">
                  <ScanLine className="w-7 h-7 mx-auto opacity-80 animate-pulse" />
                  <p className="text-sm font-bold mt-2">Inquadra la tessera sanitaria</p>
                  <p className="text-[11px] text-white/70 mt-0.5">Il documento deve essere ben illuminato</p>
                </div>
              </div>
            </div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3">
              <button
                onClick={stopCamera}
                className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur"
                data-testid="intake-camera-close"
              >
                <X className="w-4 h-4" />
              </button>
              <button
                onClick={captureFromVideo}
                disabled={scanning}
                className="w-16 h-16 rounded-full bg-white text-slate-900 hover:scale-105 transition-transform shadow-2xl flex items-center justify-center font-bold"
                data-testid="intake-camera-shutter"
              >
                <Camera className="w-6 h-6" />
              </button>
            </div>
          </div>
        ) : scanning ? (
          <ScanningOverlay />
        ) : (
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); onFile(e.dataTransfer.files?.[0]); }}
            data-testid="intake-dropzone"
            className={`rounded-3xl border-2 border-dashed p-14 cursor-pointer transition-all ${
              dragOver
                ? "border-violet-400 bg-violet-50/50 dark:bg-violet-950/20"
                : "border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/40 hover:border-violet-300"
            }`}
          >
            <input
              ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic"
              onChange={(e) => onFile(e.target.files?.[0])}
              className="hidden" data-testid="intake-file-input"
            />
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center shadow-2xl">
                <ScanLine className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900 dark:text-white">Trascina o seleziona la tessera sanitaria</p>
                <p className="text-xs text-muted-foreground mt-1">JPEG / PNG / WEBP · max 8 MB</p>
              </div>
              <p className="text-[11px] text-slate-400">— oppure —</p>
              <button
                onClick={(e) => { e.stopPropagation(); startCamera(); }}
                data-testid="intake-camera-start"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-sm hover:scale-[1.02] transition-transform"
              >
                <Camera className="w-4 h-4" /> Apri fotocamera
              </button>
              <p className="text-[10px] text-slate-400 flex items-center gap-1">
                <Smartphone className="w-3 h-3" /> Su mobile usa la fotocamera posteriore
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <SidebarCard icon={ShieldCheck} title="GDPR ready"
          desc="Audit log per ogni scansione · dati cifrati a-rest · accessi tracciati." />
        <SidebarCard icon={Sparkles} title="OCR AI Claude Sonnet 4.5"
          desc="Riconosce tessera sanitaria, carta identità, patente, passaporto." />
        <SidebarCard icon={BadgeCheck} title="Validazione CF locale"
          desc="Algoritmo italiano completo · checksum verificato · zero API esterne." />
        <SidebarCard icon={AlertCircle} title="Dedup automatico"
          desc="Confronto su codice fiscale, email e telefono prima della creazione." />
      </div>
    </div>
  );
};

const Bracket = ({ corner }) => {
  const m = {
    tl: "top-0 left-0 border-t-2 border-l-2 rounded-tl-2xl",
    tr: "top-0 right-0 border-t-2 border-r-2 rounded-tr-2xl",
    bl: "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-2xl",
    br: "bottom-0 right-0 border-b-2 border-r-2 rounded-br-2xl",
  };
  return <span className={`absolute w-10 h-10 border-white/70 ${m[corner]}`} />;
};

const SidebarCard = ({ icon: Icon, title, desc }) => (
  <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-4">
    <div className="flex items-start gap-3">
      <span className="w-8 h-8 rounded-lg bg-violet-500/10 text-violet-600 dark:text-violet-300 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <p className="text-sm font-bold">{title}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{desc}</p>
      </div>
    </div>
  </div>
);

/* ---------- Scanning overlay ---------- */
const ScanningOverlay = () => (
  <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-violet-500/5 to-cyan-500/5 p-14 text-center" data-testid="intake-scanning">
    <div className="relative mx-auto w-20 h-20">
      <div className="absolute inset-0 rounded-full border-2 border-violet-200 animate-ping" />
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center shadow-2xl">
        <ScanLine className="w-9 h-9 text-white animate-pulse" />
      </div>
    </div>
    <p className="mt-5 text-lg font-bold">Analisi documento in corso...</p>
    <p className="text-xs text-muted-foreground mt-1">L'AI sta estraendo nome, codice fiscale, data di nascita...</p>
    <div className="mt-6 max-w-xs mx-auto h-1.5 rounded-full bg-slate-200/60 dark:bg-white/10 overflow-hidden">
      <div className="h-full w-2/3 bg-gradient-to-r from-violet-500 to-cyan-500 animate-pulse" />
    </div>
  </div>
);

/* ---------- Step 2: Review ---------- */
const ReviewStep = ({ data, setData, previewUrl, duplicates, onBack, onConfirm, saving }) => {
  const cfValid = data.fiscal_code_valid;
  const update = (k, v) => setData({ ...data, [k]: v });
  return (
    <div className="space-y-5" data-testid="intake-review">
      {/* Hero card */}
      <div className="rounded-3xl bg-gradient-to-br from-violet-600 via-violet-700 to-cyan-600 p-6 text-white shadow-2xl">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80 mb-1">Step 2 di 3 · Verifica dati</p>
            <h2 className="text-3xl font-black">{data.first_name || "—"} {data.last_name || ""}</h2>
            <div className="flex items-center gap-2 mt-2 text-xs opacity-95">
              <FileText className="w-3.5 h-3.5" />
              <span className="capitalize">{(data.document_type || "documento").replace(/_/g, " ")}</span>
              <span>·</span>
              <span>OCR confidence: <strong className="capitalize">{data.confidence}</strong></span>
            </div>
          </div>
          {previewUrl && (
            <img src={previewUrl} alt="Documento" className="h-28 w-44 object-cover rounded-xl border border-white/20 shadow-xl" />
          )}
        </div>
      </div>

      {/* Duplicate warning */}
      {duplicates.length > 0 && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-4 flex items-start gap-3" data-testid="intake-duplicate-warning">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-900 dark:text-amber-200">Paziente potenzialmente già presente</p>
            <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
              Trovati {duplicates.length} pazienti con dati simili. Verifica prima di creare un duplicato.
            </p>
            <ul className="mt-2 space-y-1">
              {duplicates.slice(0, 3).map(d => (
                <li key={d.client_id} className="text-xs text-amber-900 dark:text-amber-200">
                  • <strong>{d.first_name} {d.last_name}</strong>
                  {d.fiscal_code && <span className="ml-1 font-mono opacity-80">· {d.fiscal_code}</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Editable form */}
      <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Dati anagrafici · verifica e modifica</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nome" icon={UserIcon} value={data.first_name} onChange={(v) => update("first_name", v)} testid="intake-field-first_name" />
          <Field label="Cognome" icon={UserIcon} value={data.last_name} onChange={(v) => update("last_name", v)} testid="intake-field-last_name" />
          <Field
            label="Codice Fiscale" mono icon={BadgeCheck}
            value={data.fiscal_code} onChange={(v) => update("fiscal_code", v.toUpperCase())}
            badge={data.fiscal_code ? (cfValid ? "valido" : "non valido") : null}
            badgeOk={cfValid}
            testid="intake-field-fiscal_code"
          />
          <Field label="Data di nascita" type="date" icon={Calendar} value={data.birth_date} onChange={(v) => update("birth_date", v)} testid="intake-field-birth_date" />
          <Field label="Sesso" value={data.gender} onChange={(v) => update("gender", v.toUpperCase())} testid="intake-field-gender" />
          <Field label="Luogo di nascita" icon={MapPin} value={data.birth_place} onChange={(v) => update("birth_place", v)} testid="intake-field-birth_place" />
          <Field label="Email" type="email" icon={Mail} value={data.email} onChange={(v) => update("email", v)} testid="intake-field-email" />
          <Field label="Telefono" type="tel" icon={Phone} value={data.phone} onChange={(v) => update("phone", v)} testid="intake-field-phone" />
          <Field label="Indirizzo" value={data.address} onChange={(v) => update("address", v)} testid="intake-field-address" />
          <Field label="Città" value={data.city} onChange={(v) => update("city", v)} testid="intake-field-city" />
          <Field label="N° Documento" mono value={data.doc_number} onChange={(v) => update("doc_number", v)} testid="intake-field-doc_number" />
          <Field label="Scadenza documento" type="date" value={data.doc_expiry} onChange={(v) => update("doc_expiry", v)} testid="intake-field-doc_expiry" />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onBack}
          data-testid="intake-back-btn"
          className="rounded-full px-5 py-2.5 text-sm font-bold border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 inline-flex items-center gap-1.5"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Nuovo scan
        </button>
        <button
          onClick={onConfirm}
          disabled={saving || !data.first_name || !data.last_name}
          data-testid="intake-confirm-btn"
          className="rounded-full px-7 py-3 text-sm font-bold bg-gradient-to-r from-emerald-500 to-cyan-500 hover:shadow-2xl text-white inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Conferma e crea paziente
        </button>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", icon: Icon, badge, badgeOk, mono, testid }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between gap-2 mb-1">
      <span className="flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </span>
      {badge && (
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
          badgeOk ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
            : "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
        }`}>
          {badgeOk ? <BadgeCheck className="w-2.5 h-2.5 inline mr-0.5" /> : <AlertCircle className="w-2.5 h-2.5 inline mr-0.5" />}
          {badge}
        </span>
      )}
    </label>
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-100 dark:focus:ring-violet-900/30 outline-none transition-colors ${mono ? "font-mono uppercase tracking-wider" : ""}`}
    />
  </div>
);

/* ---------- Step 3: Done ---------- */
const DoneStep = ({ client, onReset }) => (
  <div className="space-y-6 text-center" data-testid="intake-done">
    <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-2xl animate-in zoom-in">
      <CheckCircle2 className="w-10 h-10 text-white" />
    </div>
    <div>
      <h2 className="text-3xl font-black">Paziente creato.</h2>
      <p className="text-muted-foreground mt-1">
        <strong className="text-slate-900 dark:text-white">{client.first_name} {client.last_name}</strong> è ora nello studio.
      </p>
    </div>
    <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 p-5 max-w-md mx-auto text-left">
      <div className="grid grid-cols-2 gap-2 text-xs">
        {client.fiscal_code && <div><span className="text-slate-500">CF:</span> <span className="font-mono ml-1">{client.fiscal_code}</span></div>}
        {client.birth_date && <div><span className="text-slate-500">Nascita:</span> <span className="ml-1">{client.birth_date}</span></div>}
        {client.email && <div className="col-span-2"><span className="text-slate-500">Email:</span> <span className="ml-1">{client.email}</span></div>}
        {client.phone && <div className="col-span-2"><span className="text-slate-500">Telefono:</span> <span className="ml-1">{client.phone}</span></div>}
      </div>
    </div>
    <div className="flex flex-wrap items-center justify-center gap-3">
      <Link
        to={`/clients/${client.client_id}`}
        className="rounded-full px-6 py-3 text-sm font-bold bg-gradient-to-r from-violet-600 to-cyan-600 text-white hover:shadow-2xl inline-flex items-center gap-2"
        data-testid="intake-go-profile"
      >
        Vai alla scheda paziente <ArrowRight className="w-4 h-4" />
      </Link>
      <button
        onClick={onReset}
        data-testid="intake-new"
        className="rounded-full px-5 py-3 text-sm font-bold border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 inline-flex items-center gap-1.5"
      >
        <RefreshCw className="w-3.5 h-3.5" /> Nuovo paziente
      </button>
    </div>
  </div>
);

export default PatientIntakePage;
